// 协议类型：与 touranki-core 的 proto 投影、touranki-server 的 HTTP/WS 信封一一对应。
//
// 注意事项：字段名必须与 Go 侧 json tag 完全一致（snake_case）；前端不解析 id 内部结构
// （p- 玩家 / r- 房间 / g- 对局 / c- 请求），只当作不透明字符串传递。

export interface Session {
  player_id: string;
  token: string;
  name: string;
  expires_at: string | null;
}

export interface RoomPlayer {
  player_id: string;
  name: string;
  seat: number;
  online: boolean;
  ready: boolean;
  bot?: boolean;
}

export interface RoomResult {
  reason: string;
  cause: string;
  conceder: string;
  at: string;
}

export interface Room {
  room_id: string;
  name: string;
  phase: "waiting" | "running" | "finished";
  host_player_id: string;
  max_players: number;
  is_public: boolean;
  players: RoomPlayer[];
  ruleset: Ruleset;
  game_id: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  turn_player: string | null;
  turn_deadline?: string | null;
  result?: RoomResult | null;
}

export interface Ruleset {
  mode: string;
  abilities: boolean;
  reveal_condition: boolean;
  short_time: boolean;
  no_deck_refill: boolean;
  hime_kome_adjust: boolean;
}

export interface RoleBrief {
  id: string;
  name: string;
  camp: string;
}

export interface PlayerState {
  id: string;
  seat: number;
  name: string;
  alive: boolean;
  slot: number;
  damage: number;
  revealed: boolean;
  role: RoleBrief | null;
  hp?: number;
  equipment: string[];
  is_judge: boolean;
  attack_immune_until_move?: boolean;
}

export interface PlaceState {
  slot: number;
  place: string;
  name: string;
  numbers: number[];
  players: string[];
}

export interface DeckState {
  remaining: number;
  discard: number;
  discard_visible: boolean;
}

export interface WinnerState {
  player: string;
  camp: string;
}

export interface ResultState {
  reason: string;
  winners: WinnerState[];
}

export interface TurnState {
  player: string;
  step: string;
  extra_turns: number;
  extra_turn_queue?: string[];
  extra_turn_resume?: string;
}

export interface State {
  phase: string;
  mode: string;
  round: number;
  turn: TurnState;
  board: { places: PlaceState[]; ranges: [number, number][] };
  players: PlayerState[];
  decks: Record<string, DeckState>;
  result: ResultState | null;
}

export interface RoleCard {
  id: string;
  name: string;
  camp: string;
  hp: number;
  win_condition: string;
  revealed: boolean;
}

export interface KleinMemoryEntry {
  seq: number;
  direction: string;
  card_id: string;
  text: string;
  judgment?: {
    seq: number;
    giver: string;
    receiver: string;
    declaration: "obey" | "nothing";
  };
}

export interface AbilityState {
  id: string;
  usable: boolean;
  used: boolean;
  uses?: number;
  progress?: number;
}

export interface Private {
  role_card?: RoleCard;
  klein_memory?: KleinMemoryEntry[];
  abilities?: AbilityState[];
}

export interface CardBrief {
  id: string;
  text: string;
}

export interface PromptOption {
  value: unknown;
  label: string;
}

export interface MoveRoll {
  slot: number;
  d4: number;
  d6: number;
}

export interface PromptContext {
  ability?: string;
  card?: CardBrief;
  owner?: string;
  player?: string;
  candidates?: string[];
  place?: string;
  slot?: number;
  d4?: number;
  d6?: number;
  amount?: number;
  by?: string;
  effect?: string;
  reason?: string;
  handler?: string;
  params?: Record<string, string>;
  move_rolls?: MoveRoll[];
}

export interface Pending {
  prompt_id: string;
  player: string;
  kind: string;
  text: string;
  options: PromptOption[];
  allow_pass: boolean;
  context?: PromptContext;
  private_info?: { card?: CardBrief };
}

export interface GameEvent {
  seq: number;
  type: string;
  visibility: string;
  to?: string[];
  data?: Record<string, unknown>;
}

// ── WebSocket 消息（Server → 前端） ──────────────────────────────────────────

export interface WelcomeMessage {
  type: "welcome";
  player_id: string;
  name: string;
  frontend_protocol: number;
  server_time: string;
}

export interface RoomMessage {
  type: "room";
  room: Room;
}

export interface StateMessage {
  type: "state";
  seq: number;
  state: State;
  private: Private | null;
  pending: Pending | null;
  /** 该收件人此刻可以提交的 action（原样回发即可）；空数组＝只能等待。 */
  legal_actions: LegalAction[];
  /** 正在处理 pending 询问的玩家（无则空串）。 */
  waiting_for?: string;
  /** 实时动作与其结果状态原子下发；重连快照没有此字段，不回放历史演出。 */
  events?: GameEvent[];
}

/** LegalAction 与 Core 的 ActionDTO 同形：前端只负责原样回发，不推断规则。
 *  注意事项：用 type 而非 interface —— 需要能直接当作 Record<string, unknown> 传给发送层。 */
export type LegalAction = {
  [key: string]: unknown;
  type: string;
  mode?: string;
  slot?: number;
  place?: string;
  target?: string;
  targets?: string[];
  attack_all?: boolean;
  deck?: string;
  card?: string;
  ability?: string;
  params?: Record<string, string>;
  prompt_id?: string;
  value?: unknown;
};

export interface EventsMessage {
  type: "events";
  seq: number;
  events: GameEvent[];
}

export interface ResponseMessage {
  type: "response";
  request_id: string;
  ok: boolean;
  error: { code: string; message: string } | null;
}

export interface PongMessage {
  type: "pong";
  ts?: string;
}

export interface ErrorMessage {
  type: "error";
  code: string;
  message: string;
  request_id?: string;
}

export type ServerMessage =
  | WelcomeMessage
  | RoomMessage
  | StateMessage
  | EventsMessage
  | ResponseMessage
  | PongMessage
  | ErrorMessage;

// ── 前端展示用映射 ─────────────────────────────────────────────────────────

export interface PlaceStyle {
  kana: string;
}

/** 六张场所卡的展示信息：只保留读音；人物抠像由 scene_<place>.webp 提供。 */
export const PLACE_STYLE: Record<string, PlaceStyle> = {
  hakurei: { kana: "はくれいじんじゃ" },
  moriya: { kana: "もりやじんじゃ" },
  kleinji: { kana: "みょうれんじ" },
  human_village: { kana: "にんげんむら" },
  eientei: { kana: "えいえんてい" },
  magic_forest: { kana: "まほうのもり" },
};


/** 步骤名：盘面中央的“印章”；未知值也不直接暴露协议英文。 */
export const STEP_LABEL: Record<string, string> = {
  move: "移動",
  area: "場所",
  attack: "攻撃",
  place: "落位",
  respond: "応答",
  setup: "落位",
  wait: "待機",
  finished: "終局",
  game_over: "終局",
};

/** 阵营名。 */
export const CAMP_LABEL: Record<string, string> = {
  boss: "頭目",
  jiki: "自機",
  yajiuma: "野次馬",
};

export function campLabel(camp: string | null | undefined): string {
  if (!camp) return "？";
  return CAMP_LABEL[camp] ?? camp;
}
